import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Lock as LockIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  Star as StarIcon,
  Map as MapIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  DeleteOutline as DeleteOutlineIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCartStore } from '../store/cartStore';
import { Address, AddressService } from '../services/addressService';
import { useAuthStore } from '../store/authStore';
import AddressMapView from '../components/Settings/tabs/AddressMapView';
import { PlatformSettingsService } from '../services/platformSettingsService';
import { TransactionService } from '../services/transactionService';
import { supabase } from '../lib/supabase';

const formatMoney = (value: number) => {
  const v = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(v);
};

const CHECKOUT_DRAFT_KEY = 'checkout-draft-v1';

type CheckoutDraft = {
  contactEmail: string;
  fullName: string;
  address1: string;
  address2: string;
  city: string;
  zip: string;
  country: string;
  paymentMethod: 'card' | 'paypal' | 'bank';
};

const hasHouseNumber = (value: string) => {
  const v = String(value || '').trim();
  if (!v) return false;
  // Require both some letters (street name) AND a number token (e.g. 4, 12A, 221B)
  const hasLetters = /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(v);
  const hasNumberToken = /\b\d+[A-Za-z]?\b/.test(v);
  return hasLetters && hasNumberToken;
};

const safeNumber = (value: any) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { items, totalItems, totalPrice, shippingCost, grandTotal, clearCart, updateQuantity, removeItem } = useCartStore();
  const { user, isAuthenticated } = useAuthStore();

  const fromState = (location.state as any)?.from as string | undefined;

  useEffect(() => {
    if (!fromState) return;
    try {
      sessionStorage.setItem('checkout_last_from', fromState);
    } catch {
      // ignore
    }
  }, [fromState]);

  const handleBack = React.useCallback(() => {
    const safeNav = (to: string) => {
      navigate(to);
    };

    if (fromState) {
      safeNav(fromState);
      return;
    }

    try {
      const saved = sessionStorage.getItem('checkout_last_from');
      if (saved) {
        safeNav(saved);
        return;
      }
    } catch {
      // ignore
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    safeNav('/all-products');
  }, [fromState, navigate]);

  const [contactEmail, setContactEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('DE');
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal' | 'bank'>('card');
  const [submitting, setSubmitting] = useState(false);
  const [addressPickerOpen, setAddressPickerOpen] = useState(false);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [mapPickerFullscreen, setMapPickerFullscreen] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState<Address[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);

  // Restore draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(CHECKOUT_DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<CheckoutDraft>;
      if (typeof parsed.contactEmail === 'string') setContactEmail(parsed.contactEmail);
      if (typeof parsed.fullName === 'string') setFullName(parsed.fullName);
      if (typeof parsed.address1 === 'string') setAddress1(parsed.address1);
      if (typeof parsed.address2 === 'string') setAddress2(parsed.address2);
      if (typeof parsed.city === 'string') setCity(parsed.city);
      if (typeof parsed.zip === 'string') setZip(parsed.zip);
      if (typeof parsed.country === 'string') setCountry(parsed.country);
      if (parsed.paymentMethod === 'card' || parsed.paymentMethod === 'paypal' || parsed.paymentMethod === 'bank') {
        setPaymentMethod(parsed.paymentMethod);
      }
    } catch {
      // ignore
    } finally {
      setDraftLoaded(true);
    }
  }, []);

  // Auto-fill email from authenticated user if the field is empty
  useEffect(() => {
    if (!draftLoaded) return;
    if (contactEmail.trim()) return;
    const email = (user as any)?.email;
    if (isAuthenticated && typeof email === 'string' && email.includes('@')) {
      setContactEmail(email);
    }
  }, [draftLoaded, contactEmail, isAuthenticated, user]);

  // Persist draft as user types
  useEffect(() => {
    if (!draftLoaded) return;
    const draft: CheckoutDraft = {
      contactEmail,
      fullName,
      address1,
      address2,
      city,
      zip,
      country,
      paymentMethod,
    };
    try {
      localStorage.setItem(CHECKOUT_DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // ignore
    }
  }, [contactEmail, fullName, address1, address2, city, zip, country, paymentMethod]);

  const orderLines = useMemo(() => {
    return items.map((item) => {
      const p: any = item.product as any;
      const title = String(p.title || p.name || 'Untitled');
      const image = Array.isArray(p.images) && p.images.length > 0 ? p.images[0] : (p.image || '/placeholder-product.jpg');
      const unitPrice = Number.isFinite(p.price) ? Number(p.price) : 0;
      const perItemShipping = Number.isFinite(item.shippingCost as any) ? Number(item.shippingCost) : 0;
      const lineSubtotal = unitPrice * item.quantity;
      const lineShipping = perItemShipping * item.quantity;
      const productId = p.id;
      return {
        id: item.id,
        title,
        image,
        productId,
        unitPrice,
        quantity: item.quantity,
        perItemShipping,
        lineSubtotal,
        lineShipping,
      };
    });
  }, [items]);

  const loadSavedAddresses = async () => {
    try {
      setLoadingAddresses(true);
      const data = await AddressService.getUserAddresses();
      setSavedAddresses(Array.isArray(data) ? data : []);
    } catch (e: any) {
      console.error('Failed to load saved addresses:', e);
      toast.error(e?.message || 'Failed to load addresses', { position: 'top-center' });
      setSavedAddresses([]);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const openAddressPicker = async () => {
    setAddressPickerOpen(true);
    await loadSavedAddresses();
  };

  const applyAddress = (a: Address) => {
    setFullName(a.full_name || fullName);
    setAddress1(a.address_line_1 || '');
    setAddress2(a.address_line_2 || '');
    setCity(a.city || '');
    setZip(a.postal_code || '');
    setCountry(a.country || 'DE');
    setAddressPickerOpen(false);
    toast.success('Shipping address selected', { position: 'top-center' });
  };

  const applyMapAddress = (addr: {
    address_line_1: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    latitude: number;
    longitude: number;
  }) => {
    setAddress1(addr.address_line_1 || '');
    setAddress2('');
    setCity(addr.city || '');
    setZip(addr.postal_code || '');
    setCountry(addr.country || '');
    setMapPickerOpen(false);
    toast.success('Address selected from map', { position: 'top-center' });
  };

  const addressMissingHouseNumber = useMemo(() => {
    const v = address1.trim();
    if (!v) return false;
    return !hasHouseNumber(v);
  }, [address1]);

  const isFormValid = useMemo(() => {
    // Email is optional, but if entered it must look valid
    const email = contactEmail.trim();
    const emailOk = email.length === 0 || email.includes('@');
    return (
      emailOk &&
      fullName.trim().length >= 2 &&
      address1.trim().length >= 3 &&
      hasHouseNumber(address1) &&
      city.trim().length >= 2 &&
      zip.trim().length >= 3 &&
      country.trim().length >= 2
    );
  }, [contactEmail, fullName, address1, city, zip, country]);

  const handlePlaceOrder = async () => {
    if (items.length === 0) {
      toast.error('Your cart is empty', { position: 'top-center' });
      return;
    }
    if (!isFormValid) {
      const addr = address1.trim();
      if (!addr) {
        toast.error('Please enter your street + house number', { position: 'top-center' });
        return;
      }
      if (!hasHouseNumber(addr)) {
        toast.error('Please add a house/building number to your address', { position: 'top-center' });
        return;
      }
      if (!city.trim()) {
        toast.error('Please enter a city', { position: 'top-center' });
        return;
      }
      if (!zip.trim()) {
        toast.error('Please enter a ZIP / postal code', { position: 'top-center' });
        return;
      }
      if (!country.trim()) {
        toast.error('Please enter a country', { position: 'top-center' });
        return;
      }
      toast.error('Please complete your shipping details', { position: 'top-center' });
      return;
    }

    setSubmitting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';

      const sellerIds = Array.from(
        new Set(
          items
            .map((it) => (it?.product as any)?.sellerId)
            .filter((v) => typeof v === 'string' && v.trim().length > 0)
        )
      );

      let vendorEmail = sellerIds.length === 1 ? '' : 'multi-vendor';
      if (sellerIds.length === 1) {
        const { data: vendorRow, error: vendorErr } = await supabase
          .from('users')
          .select('email')
          .eq('id', sellerIds[0])
          .single();
        if (vendorErr) throw vendorErr;
        vendorEmail = String(vendorRow?.email || '').trim();
        if (!vendorEmail) throw new Error('Vendor email not found');
      }

      let platformEmail = '';
      try {
        const defaultMethod = await PlatformSettingsService.getDefaultPlatformPaymentMethod();
        if (defaultMethod?.payment_method_email) {
          platformEmail = defaultMethod.payment_method_email.trim();
        } else {
          const allMethods = await PlatformSettingsService.getPlatformPaymentMethods();
          const paypalMethod = allMethods.find((m) => m.payment_method_type === 'paypal' && m.payment_method_email);
          if (paypalMethod?.payment_method_email) platformEmail = paypalMethod.payment_method_email.trim();
        }
      } catch (e) {
        console.warn('Failed to load platform payment method:', e);
      }
      if (!platformEmail) platformEmail = 'platform';

      const transactionPayload = {
        created_from: 'checkout',
        cart: {
          items: items.map((it) => ({
            id: it.id,
            quantity: it.quantity,
            shippingCost: safeNumber((it as any).shippingCost),
            product: {
              id: it.product?.id,
              title: (it.product as any)?.title,
              price: safeNumber((it.product as any)?.price),
              sellerId: (it.product as any)?.sellerId,
            },
          })),
          totals: {
            totalPrice: safeNumber(totalPrice),
            shippingCost: safeNumber(shippingCost),
            grandTotal: safeNumber(grandTotal),
            currency: 'EUR',
          },
        },
        shipping: {
          contactEmail: contactEmail.trim() || null,
          fullName: fullName.trim(),
          address1: address1.trim(),
          address2: address2.trim() || null,
          city: city.trim(),
          zip: zip.trim(),
          country: country.trim(),
        },
        sellers: sellerIds,
      };

      const totalAmount = safeNumber(grandTotal);

      const transaction = await TransactionService.createTransaction({
        transaction_type: 'test',
        is_test_payment: true,
        product_id: null,
        order_id: null,
        vendor_email: vendorEmail,
        platform_email: platformEmail,
        vendor_amount: totalAmount,
        platform_amount: 0,
        total_amount: totalAmount,
        currency: 'EUR',
        status: 'completed',
        payment_method: paymentMethod,
        payer_id: sessionData?.session?.user?.id || undefined,
        payer_email: sessionData?.session?.user?.email || undefined,
        metadata: transactionPayload,
      });

      if (!token) {
        throw new Error('Not authenticated');
      }

      const resp = await fetch('/api/store-transaction.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ transaction }),
      });

      const respJson = await resp.json().catch(() => null);
      if (!resp.ok || !respJson?.ok) {
        throw new Error(respJson?.error || 'Failed to write transaction file');
      }

      try {
        const buyerEmail = (sessionData?.session?.user?.email || contactEmail || '').trim();
        if (buyerEmail) {
          let sellerLabelById: Record<string, string> = {};
          try {
            const uniqSellerIds = Array.from(
              new Set(
                items
                  .map((it) => String((it.product as any)?.sellerId || '').trim())
                  .filter(Boolean)
              )
            );
            if (uniqSellerIds.length > 0) {
              const { data: sellers } = await supabase
                .from('users')
                .select('id,first_name,last_name,alias,email')
                .in('id', uniqSellerIds);
              (sellers || []).forEach((s: any) => {
                const id = String(s?.id || '').trim();
                if (!id) return;
                const alias = String(s?.alias || '').trim();
                const full = `${String(s?.first_name || '').trim()} ${String(s?.last_name || '').trim()}`.trim();
                const email = String(s?.email || '').trim();
                sellerLabelById[id] = alias || full || email || id;
              });
            }
          } catch {
            sellerLabelById = {};
          }

          const products = items.map((it) => {
            const p: any = it.product as any;
            const sellerId = String(p?.sellerId || '').trim();
            const seller = sellerLabelById[sellerId] || sellerId;
            const name = String(p?.title || p?.name || 'Item');
            return {
              id: p?.id,
              title: name,
              name,
              quantity: it.quantity,
              sellerId: sellerId || undefined,
              seller: seller || undefined,
            };
          });
          const productsSummary = products
            .map((p) => `${String(p.title || p.name || 'Item')} × ${Number(p.quantity) || 1}`)
            .join('\n');

          void fetch('/api/notifications-dispatch.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event: 'transaction_completed',
              user: {
                id: sessionData?.session?.user?.id || '',
                email: buyerEmail,
                firstName: fullName.trim().split(' ')[0] || '',
              },
              transaction: {
                id: transaction.id,
                total: String(totalAmount.toFixed(2)),
                currency: 'EUR',
                productsSummary,
                productsCount: String(products.length),
              },
              products,
            }),
          }).catch(() => {
            // best-effort
          });
        }
      } catch {
        // best-effort
      }

      const buyerId = sessionData?.session?.user?.id || '';
      if (buyerId) {
        const buyerEmailSnapshot = String(sessionData?.session?.user?.email || contactEmail || '').trim();
        const buyerNameSnapshot = fullName.trim();

        const purchaseRows = items.map((it) => {
          const p: any = it.product as any;
          const sellerId = String(p?.sellerId || '').trim();
          const productId = String(p?.id || '').trim();
          if (!sellerId || !productId) {
            throw new Error('Missing product seller information');
          }

          const sellerNameSnapshot = String(p?.sellerName || p?.seller || '').trim();

          return {
            offer_id: null,
            transaction_id: transaction.id,
            product_id: productId,
            buyer_id: buyerId,
            buyer_email: buyerEmailSnapshot || null,
            buyer_name: buyerNameSnapshot || null,
            seller_id: sellerId,
            seller_name: sellerNameSnapshot || null,
            purchase_price: safeNumber(p?.price),
            paid_status: 'succeeded',
            delivery_status: 'pending',
            tracking_number: null,
            product_name: String(p?.title || ''),
            product_description: p?.description ?? null,
            product_images: Array.isArray(p?.images) ? p.images : [],
            product_colors: Array.isArray(p?.color) ? p.color : (Array.isArray(p?.colors) ? p.colors : []),
            product_custom_colors: Array.isArray(p?.custom_colors) ? p.custom_colors : [],
            product_sizes: Array.isArray(p?.size) ? p.size : (Array.isArray(p?.sizes) ? p.sizes : []),
            product_condition: p?.condition ?? null,
            product_brand: p?.brand ?? null,
            product_model: p?.model ?? null,
            product_sku: p?.sku ?? null,
            product_tags: Array.isArray(p?.tags) ? p.tags : [],
          };
        });

        const { data: insertedPurchases, error: purchasesError } = await supabase
          .from('purchases')
          .insert(purchaseRows)
          .select();

        if (purchasesError) {
          throw new Error(purchasesError.message || 'Failed to create purchase records');
        }

        // Seller notification (best-effort): one email per seller summarizing items sold
        try {
          const uniqSellerIds = Array.from(new Set(purchaseRows.map((r: any) => String(r.seller_id || '').trim()).filter(Boolean)));
          let sellerById: Record<string, any> = {};
          try {
            if (uniqSellerIds.length > 0) {
              const { data: sellers } = await supabase
                .from('users')
                .select('id,email,first_name,last_name,alias')
                .in('id', uniqSellerIds);
              (sellers || []).forEach((s: any) => {
                const id = String(s?.id || '').trim();
                if (!id) return;
                sellerById[id] = s;
              });
            }
          } catch {
            sellerById = {};
          }

          const buyerEmail = String(sessionData?.session?.user?.email || contactEmail || '').trim();
          const buyerName = fullName.trim();

          const bySeller: Record<string, Array<{ name: string; quantity: number; price: number }>> = {};
          items.forEach((it) => {
            const p: any = it.product as any;
            const sid = String(p?.sellerId || '').trim();
            if (!sid) return;
            const name = String(p?.title || p?.name || 'Item');
            const qty = Number(it.quantity) || 1;
            const price = Number(p?.price) || 0;
            if (!bySeller[sid]) bySeller[sid] = [];
            bySeller[sid].push({ name, quantity: qty, price });
          });

          await Promise.all(
            Object.keys(bySeller).map(async (sid) => {
              const s = sellerById[sid] || {};
              const sellerEmail = String(s?.email || '').trim();
              if (!sellerEmail) return;
              const sellerFirst = String(s?.first_name || '').trim() || String(s?.alias || '').trim() || 'there';
              const lines = (bySeller[sid] || []).map((x) => `${x.name} × ${x.quantity}`);
              const itemsSummary = lines.join('\n');
              const itemsCount = String(lines.length);
              const totalSeller = (bySeller[sid] || []).reduce((acc, x) => acc + (Number(x.price) * Number(x.quantity)), 0);
              const itemsSummaryShort = lines.length > 1 ? `${lines[0]} +${lines.length - 1} more` : (lines[0] || 'Item');

              return fetch('/api/notifications-dispatch.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  event: 'product_sold',
                  user: {
                    id: sid,
                    email: sellerEmail,
                    firstName: sellerFirst,
                  },
                  buyer: {
                    id: buyerId,
                    email: buyerEmail,
                    name: buyerName,
                  },
                  purchase: {
                    total: String(totalSeller.toFixed(2)),
                    currency: 'EUR',
                    itemsSummary,
                    itemsCount,
                    itemsSummaryShort,
                  },
                  transaction: {
                    id: transaction.id,
                    currency: 'EUR',
                  },
                  products: (bySeller[sid] || []).map((x) => ({ name: x.name, quantity: x.quantity, sellerId: sid })),
                }),
              }).catch(() => null);
            })
          );
        } catch {
          // best-effort
        }

        for (const purchase of insertedPurchases) {
          const pr = await fetch('/api/store-purchase.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ purchase }),
          });
          const prJson = await pr.json().catch(() => null);
          if (!pr.ok || !prJson?.ok) {
            throw new Error(prJson?.error || 'Failed to write purchase file');
          }
        }
      }

      try {
        localStorage.removeItem(CHECKOUT_DRAFT_KEY);
      } catch {
        // ignore
      }

      clearCart();
      toast.success('Order placed!', { position: 'top-center' });
      navigate('/payment-success');
    } catch (e: any) {
      console.error('Place order failed:', e);
      toast.error(e?.message || 'Failed to place order', { position: 'top-center' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 } }}>
      <Box
        sx={(t) => ({
          mb: { xs: 2, md: 3 },
          p: { xs: 2, md: 2.5 },
          borderRadius: 4,
          border: `1px solid ${t.palette.divider}`,
          backgroundColor: t.palette.background.paper,
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={(t) => ({
                borderRadius: 999,
                px: 1.5,
                py: 0.9,
                fontWeight: 900,
                textTransform: 'none',
                border: `1px solid ${t.palette.divider}`,
                color: t.palette.text.primary,
                backgroundColor: t.palette.background.paper,
                '&:hover': {
                  backgroundColor: t.palette.action.hover,
                },
              })}
            >
              Zurück
            </Button>

            <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1.1 }}>
              Checkout
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {totalItems} item{totalItems === 1 ? '' : 's'} • Secure checkout
            </Typography>
          </Box>

          <Chip
            icon={<LockIcon />}
            label="Encrypted & secure"
            sx={{
              fontWeight: 900,
              borderRadius: 999,
              px: 0.5,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        </Box>
      </Box>

      {items.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Your cart is empty.
            </Alert>
            <Button variant="contained" onClick={() => navigate('/all-products')} sx={{ fontWeight: 900 }}>
              Browse products
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={2.5}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        color: '#6d28d9',
                        background: 'linear-gradient(180deg, rgba(124,58,237,0.14) 0%, rgba(124,58,237,0.06) 100%)',
                        border: '1px solid rgba(124,58,237,0.18)',
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 950 }}>
                      Contact
                    </Typography>
                  </Box>

                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 7 }}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        autoComplete="email"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <TextField
                        fullWidth
                        label="Full name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        autoComplete="name"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          display: 'grid',
                          placeItems: 'center',
                          color: '#0284c7',
                          background: 'linear-gradient(180deg, rgba(2,132,199,0.14) 0%, rgba(2,132,199,0.06) 100%)',
                          border: '1px solid rgba(2,132,199,0.18)',
                        }}
                      >
                        <LocalShippingIcon fontSize="small" />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 950 }}>
                        Shipping address
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setMapPickerOpen(true)}
                        startIcon={<MapIcon />}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 900,
                          textTransform: 'none',
                          borderColor: 'rgba(124,58,237,0.28)',
                          color: '#6d28d9',
                          background: 'rgba(255,255,255,0.7)',
                          '&:hover': {
                            borderColor: 'rgba(124,58,237,0.45)',
                            background: 'rgba(255,255,255,0.92)',
                          },
                        }}
                      >
                        Choose on map
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={openAddressPicker}
                        startIcon={<LocationOnIcon />}
                        sx={{
                          borderRadius: 999,
                          fontWeight: 900,
                          textTransform: 'none',
                          borderColor: 'rgba(2,132,199,0.28)',
                          color: '#0284c7',
                          background: 'rgba(255,255,255,0.7)',
                          '&:hover': {
                            borderColor: 'rgba(2,132,199,0.45)',
                            background: 'rgba(255,255,255,0.92)',
                          },
                        }}
                      >
                        Choose saved address
                      </Button>
                    </Box>
                  </Box>

                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Address"
                        value={address1}
                        onChange={(e) => setAddress1(e.target.value)}
                        autoComplete="address-line1"
                        error={address1.trim().length > 0 && addressMissingHouseNumber}
                        helperText={
                          address1.trim().length > 0 && addressMissingHouseNumber
                            ? 'Please include a house/building number (e.g., “Musterstraße 12”).'
                            : undefined
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Apartment, suite, etc. (optional)"
                        value={address2}
                        onChange={(e) => setAddress2(e.target.value)}
                        autoComplete="address-line2"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="City"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        autoComplete="address-level2"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        fullWidth
                        label="ZIP"
                        value={zip}
                        onChange={(e) => setZip(e.target.value)}
                        autoComplete="postal-code"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        fullWidth
                        label="Country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        autoComplete="country"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        color: '#111827',
                        background: 'linear-gradient(180deg, rgba(17,24,39,0.10) 0%, rgba(17,24,39,0.04) 100%)',
                        border: '1px solid rgba(0,0,0,0.10)',
                      }}
                    >
                      <PaymentIcon fontSize="small" />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 950 }}>
                      Payment
                    </Typography>
                  </Box>

                  <FormControl>
                    <RadioGroup
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value as any)}
                    >
                      <FormControlLabel value="card" control={<Radio />} label="Card (recommended)" />
                      <FormControlLabel value="paypal" control={<Radio />} label="PayPal" />
                      <FormControlLabel value="bank" control={<Radio />} label="Bank transfer" />
                    </RadioGroup>
                  </FormControl>

                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    Payment processing is currently a demo. Clicking “Place order” will clear your cart and redirect to the success page.
                  </Alert>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Card sx={{ position: { md: 'sticky' }, top: { md: 18 } }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 950, mb: 1.25 }}>
                  Order summary
                </Typography>

                <Stack spacing={1.2} sx={{ mb: 1.5 }}>
                  {orderLines.map((line) => (
                    <Box
                      key={line.id}
                      onClick={() => {
                        if (!line.productId) return;
                        navigate(`/product/${line.productId}`);
                      }}
                      role={line.productId ? 'button' : undefined}
                      tabIndex={line.productId ? 0 : undefined}
                      onKeyDown={(e) => {
                        if (!line.productId) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/product/${line.productId}`);
                        }
                      }}
                      sx={(t) => ({
                        display: 'flex',
                        gap: 1.25,
                        alignItems: 'center',
                        p: 1.15,
                        borderRadius: 3,
                        border: `1px solid ${t.palette.divider}`,
                        backgroundColor: t.palette.background.paper,
                        cursor: line.productId ? 'pointer' : 'default',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                        '&:hover': line.productId
                          ? {
                              borderColor: t.palette.primary.main,
                              boxShadow: '0 14px 40px rgba(17,24,39,0.10)',
                              transform: 'translateY(-1px)',
                            }
                          : undefined,
                      })}
                    >
                      <Avatar
                        variant="rounded"
                        src={line.image}
                        sx={{ width: 54, height: 54, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950, lineHeight: 1.15 }} noWrap>
                          {line.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {line.quantity} × {formatMoney(line.unitPrice)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Shipping: {formatMoney(line.perItemShipping)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
                        <Typography sx={{ fontWeight: 950 }}>{formatMoney(line.lineSubtotal)}</Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              borderRadius: 999,
                              border: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: 'background.paper',
                              overflow: 'hidden',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateQuantity(line.id, line.quantity - 1);
                              }}
                              sx={{ width: 30, height: 30 }}
                              aria-label="Decrease quantity"
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ width: 26, textAlign: 'center', fontWeight: 950, fontSize: 12 }}>
                              {line.quantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateQuantity(line.id, line.quantity + 1);
                              }}
                              sx={{ width: 30, height: 30 }}
                              aria-label="Increase quantity"
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeItem(line.id);
                            }}
                            sx={(t) => ({
                              width: 30,
                              height: 30,
                              borderRadius: 2,
                              border: `1px solid ${t.palette.error.main}2A`,
                              color: t.palette.error.main,
                              backgroundColor: t.palette.error.main + '14',
                              '&:hover': {
                                backgroundColor: t.palette.error.main + '1F',
                              },
                            })}
                            aria-label="Remove item"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                <Divider sx={{ my: 1.25 }} />

                <Stack spacing={0.75}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950 }}>{formatMoney(totalPrice)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Shipping</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950 }}>{formatMoney(shippingCost)}</Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 1.25 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.25 }}>
                  <Typography sx={{ fontWeight: 950 }}>Total</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 1000 }}>
                    {formatMoney(grandTotal)}
                  </Typography>
                </Box>

                {!isFormValid && (
                  <Alert severity="info" sx={{ mb: 1.25 }}>
                    Add your contact + shipping details to enable checkout.
                  </Alert>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handlePlaceOrder}
                  disabled={submitting || !isFormValid || items.length === 0}
                  startIcon={<LockIcon />}
                  sx={{
                    py: 1.35,
                    fontWeight: 1000,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
                    },
                  }}
                >
                  {submitting ? 'Placing order…' : `Place order • ${formatMoney(grandTotal)}`}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => navigate('/all-products')}
                  sx={{ mt: 0.75, fontWeight: 900, textTransform: 'none' }}
                >
                  Continue shopping
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: 'block' }}>
                  Payment method: {paymentMethod.toUpperCase()}.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Dialog
        open={addressPickerOpen}
        onClose={() => setAddressPickerOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 1000, lineHeight: 1.1 }}>Choose an address</Typography>
            <Typography variant="caption" color="text.secondary">
              Pick one of your saved addresses
            </Typography>
          </Box>
          <IconButton onClick={() => setAddressPickerOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loadingAddresses ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : savedAddresses.length === 0 ? (
            <Alert severity="info">
              No saved addresses found. Add one in Settings → Addresses.
            </Alert>
          ) : (
            <Stack spacing={1.25}>
              {savedAddresses.map((a) => (
                <Card
                  key={a.id}
                  onClick={() => applyAddress(a)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 3,
                    border: a.is_default ? '2px solid rgba(124,58,237,0.55)' : '1px solid rgba(0,0,0,0.08)',
                    background: a.is_default
                      ? 'linear-gradient(180deg, rgba(124,58,237,0.06) 0%, rgba(255,255,255,0.98) 100%)'
                      : 'rgba(255,255,255,0.95)',
                    boxShadow: '0 8px 24px rgba(2,132,199,0.08)',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 12px 30px rgba(2,132,199,0.12)',
                    },
                    transition: 'all 0.18s ease',
                  }}
                >
                  <CardContent sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 3,
                        bgcolor: a.is_default ? '#6d28d9' : '#0284c7',
                      }}
                    >
                      <LocationOnIcon />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontWeight: 1000 }} noWrap>
                          {a.title || 'Address'}
                        </Typography>
                        {a.is_default && (
                          <Chip
                            size="small"
                            icon={<StarIcon />}
                            label="Default"
                            sx={{
                              fontWeight: 900,
                              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                              color: 'white',
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {a.full_name}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.45 }}>
                        {[a.address_line_1, a.address_line_2].filter(Boolean).join(', ')}
                        <br />
                        {[a.postal_code, a.city].filter(Boolean).join(' ')}
                        {a.country ? ` • ${a.country}` : ''}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            variant="outlined"
            onClick={loadSavedAddresses}
            disabled={loadingAddresses}
            sx={{ borderRadius: 999, fontWeight: 900, textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="text"
            onClick={() => setAddressPickerOpen(false)}
            sx={{ borderRadius: 999, fontWeight: 900, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mapPickerOpen}
        onClose={() => setMapPickerOpen(false)}
        fullScreen={mapPickerFullscreen}
        fullWidth
        maxWidth={mapPickerFullscreen ? false : 'md'}
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 30px 90px rgba(17,24,39,0.22)',
          },
        }}
      >
        <Box sx={{ height: mapPickerFullscreen ? '100%' : { xs: '75vh', md: '78vh' }, minHeight: mapPickerFullscreen ? 0 : 520 }}>
          <AddressMapView
            onBack={() => setMapPickerOpen(false)}
            onClose={() => setMapPickerOpen(false)}
            onAddressSelect={applyMapAddress}
            isFullscreen={mapPickerFullscreen}
            onToggleFullscreen={() => setMapPickerFullscreen((v) => !v)}
          />
        </Box>
      </Dialog>
    </Container>
  );
};

export default CheckoutPage;
